import type { PropagatableGuidancePrimitive } from 'api-validations/locutus/guidance'

export enum StepKind {
  Details = 'details',
  Lines = 'lines',
  Regions = 'regions',
  Overrides = 'overrides'
}

export const STEP_ORDER = [
  StepKind.Details,
  StepKind.Lines,
  StepKind.Regions,
  StepKind.Overrides
] as const

export enum LinesActivityKind {
  Browsing = 'browsing',
  Drawing = 'drawing',
  PickingSide = 'pickingSide'
}

export enum DraftMode {
  Create = 'create',
  Edit = 'edit'
}

export type PickedSide = { id: string; label: string }

export type Draft = { zoneId: string; lineId: string; mode: DraftMode }

export type LinesActivity =
  | { kind: LinesActivityKind.Browsing }
  | { kind: LinesActivityKind.Drawing; sketch: PropagatableGuidancePrimitive | undefined }
  | { kind: LinesActivityKind.PickingSide; picked: PickedSide | null }

export type WizardStep =
  | { kind: StepKind.Details }
  | { kind: StepKind.Lines; previewLineId: string | null; activity: LinesActivity }
  | { kind: StepKind.Regions; draft: Draft | null }
  | { kind: StepKind.Overrides }

const ENTRY_STEPS: Record<StepKind, WizardStep> = {
  [StepKind.Details]: { kind: StepKind.Details },
  [StepKind.Lines]: {
    kind: StepKind.Lines,
    previewLineId: null,
    activity: { kind: LinesActivityKind.Browsing }
  },
  [StepKind.Regions]: { kind: StepKind.Regions, draft: null },
  [StepKind.Overrides]: { kind: StepKind.Overrides }
}

export const enterStep = (kind: StepKind): WizardStep => ENTRY_STEPS[kind]

export const isIdle = (step: WizardStep): boolean => {
  if (step.kind === StepKind.Lines) return step.activity.kind === LinesActivityKind.Browsing
  if (step.kind === StepKind.Regions) return step.draft === null
  return true
}

export enum MapMode {
  Details = 'details',
  LinesBrowsing = 'linesBrowsing',
  LinesDrawing = 'linesDrawing',
  LinesPickingSide = 'linesPickingSide',
  Regions = 'regions',
  RegionsDraft = 'regionsDraft',
  Overrides = 'overrides'
}

const LINES_MAP_MODES: Record<LinesActivityKind, MapMode> = {
  [LinesActivityKind.Browsing]: MapMode.LinesBrowsing,
  [LinesActivityKind.Drawing]: MapMode.LinesDrawing,
  [LinesActivityKind.PickingSide]: MapMode.LinesPickingSide
}

export const toMapMode = (step: WizardStep): MapMode => {
  if (step.kind === StepKind.Lines) return LINES_MAP_MODES[step.activity.kind]
  if (step.kind === StepKind.Regions) {
    return step.draft === null ? MapMode.Regions : MapMode.RegionsDraft
  }
  if (step.kind === StepKind.Overrides) return MapMode.Overrides
  return MapMode.Details
}
